import type { HttpClient } from '~/shared/api'
import { ApiService, toModel, toModels } from '~/shared/api'
import type { Logger } from '~/shared/interfaces'
import { DigitalLectureDetail, DigitalLectureVideoCard } from '~/models/digital-lecture'
import { API_PREFIX } from '~/shared/const/request-services'

export class DigitalLectureDetailService extends ApiService {
  constructor(
    http: HttpClient,
    private readonly logger: Logger
  ) {
    super(http)
  }

  readonly urls = {
    bySlug: (slug: string) => `${API_PREFIX}/digital-lecture/videos/${slug}`,
    random: `${API_PREFIX}/digital-lecture/videos/random`,
    like: (slug: string) => `${API_PREFIX}/digital-lecture/videos/${slug}/like`,
    pageView: (slug: string) => `${API_PREFIX}/digital-lecture/videos/${slug}/page-view`,
    view: (slug: string) => `${API_PREFIX}/digital-lecture/videos/${slug}/view`,
  }

  getBySlug(slug: string): Promise<DigitalLectureDetail> {
    return this.http.request({ url: this.urls.bySlug(slug) }).then(toModel(DigitalLectureDetail))
  }

  getRandom(excludeIds: number[] = []): Promise<DigitalLectureVideoCard[]> {
    const params: Record<string, string | number> = {}
    if (excludeIds.length) {
      params['filter[exclude_ids]'] = excludeIds.join(',')
    }

    return this.http
      .request({ url: this.urls.random, params })
      .then(toModels(DigitalLectureVideoCard))
  }

  async addLike(slug: string): Promise<boolean> {
    try {
      const res = await this.http.request<{ data?: { success?: boolean } }>({
        method: 'POST',
        url: this.urls.like(slug),
      })
      return Boolean(res.data?.data?.success)
    } catch (error) {
      this.logger.warn(`Failed to like "${slug}"`, error)
      return false
    }
  }

  async addPageView(slug: string): Promise<void> {
    try {
      await this.http.request({ method: 'POST', url: this.urls.pageView(slug) })
    } catch (error) {
      this.logger.warn(`Failed to count page-view for "${slug}"`, error)
    }
  }

  async addView(slug: string, phone?: string | null): Promise<boolean> {
    try {
      await this.http.request({
        method: 'POST',
        url: this.urls.view(slug),
        params: phone ? { ph: phone } : undefined,
      })
      return true
    } catch (error) {
      this.logger.warn(`Failed to add view for "${slug}"`, error)
      return false
    }
  }
}
